import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Get platform holidays for a year
export const usePlatformHolidays = (year) => {
  return useQuery({
    queryKey: ['platform-holidays', year],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_platform_holidays_for_year', { target_year: year });
      if (error) throw error;
      return data;
    },
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });
};

// Get branch closures
export const useBranchClosures = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['branch-closures', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_closures')
        .select('*')
        .eq('branch_id', branchId)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!branchId,
    ...options,
  });
};

// Get all closures for a branch in a date range (combined)
export const useClosuresInRange = (branchId, startDate, endDate) => {
  return useQuery({
    queryKey: ['closures-range', branchId, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_branch_closures_in_range', {
          p_branch_id: branchId,
          p_start_date: startDate,
          p_end_date: endDate,
        });
      if (error) throw error;
      return data;
    },
    enabled: !!branchId && !!startDate && !!endDate,
  });
};

// Create branch closure
export const useCreateBranchClosure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (closure) => {
      const { data, error } = await supabase
        .from('branch_closures')
        .insert(closure)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branch-closures', data.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['closures-range'] });
      queryClient.invalidateQueries({ queryKey: ['branch-closure-date'] });
      // Invalidate absences cache since closure creates auto-absences
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });
};

// Update branch closure
export const useUpdateBranchClosure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('branch_closures')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branch-closures', data.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['closures-range'] });
      queryClient.invalidateQueries({ queryKey: ['branch-closure-date'] });
      // Invalidate absences cache since closure update modifies auto-absences
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });
};

// Delete branch closure
export const useDeleteBranchClosure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, branchId }) => {
      const { error } = await supabase
        .from('branch_closures')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { id, branchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branch-closures', data.branchId] });
      queryClient.invalidateQueries({ queryKey: ['closures-range'] });
      queryClient.invalidateQueries({ queryKey: ['branch-closure-date'] });
      // Invalidate absences cache since closure delete removes auto-absences
      queryClient.invalidateQueries({ queryKey: ['absences'] });
    },
  });
};

// Check if a specific date is a branch closure
export const useBranchClosureForDate = (branchId, date, options = {}) => {
  return useQuery({
    queryKey: ['branch-closure-date', branchId, date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_closures')
        .select('*')
        .eq('branch_id', branchId)
        .lte('start_date', date)
        .gte('end_date', date)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data; // Returns closure object or null
    },
    enabled: !!branchId && !!date,
    ...options,
  });
};
